import { loadConfig, loadMetafieldConfig, loadLegacyMetafieldConfig, assertMatchingLegacyFields, normalizeValues, buildProperties, createEngine, uploadArtwork } from './pe-mem-modal-engine.js?v=10';

class PeMemModal extends HTMLElement {
  connectedCallback() {
    if (this.connected) return;
    this.connected = true;
    this.abort = new AbortController();
    this.renderToken = 0;
    this.inputs = new Map();
    this.optionInputs = [];
    this.values = {};
    this.product = JSON.parse(this.querySelector('[data-pe-mem-product]').textContent);
    this.messages = JSON.parse(this.querySelector('[data-pe-mem-messages]').textContent);
    this.section = document.getElementById(`MainProduct-${this.dataset.sectionId}`) || this.closest('.shopify-section');
    this.dialog = this.querySelector('[data-pe-mem-dialog]');
    this.previewButton = this.querySelector('[data-pe-mem-preview]');
    this.mainAddButton = this.querySelector('[data-pe-mem-add]');
    this.confirmButton = this.querySelector('[data-pe-mem-confirm]');
    this.quantityInput = this.querySelector('[data-pe-mem-quantity]');
    this.quantityMinus = this.querySelector('[data-pe-mem-quantity-minus]');
    this.quantityPlus = this.querySelector('[data-pe-mem-quantity-plus]');
    this.section?.classList.add('pe-mem-modal-owned');
    this.nativePrice = document.getElementById(`price-${this.dataset.sectionId}`);
    this.nativePrice?.setAttribute('data-pe-mem-replaced-price', '');
    const on = (node, name, fn, capture = false) => node?.addEventListener(name, fn, { signal: this.abort.signal, capture });
    on(this.previewButton, 'click', () => this.preview());
    on(this.mainAddButton, 'click', () => this.requestAdd());
    on(this.confirmButton, 'click', () => this.add());
    on(this.querySelector('[data-pe-mem-retry]'), 'click', () => this.load());
    on(this.querySelector('[data-pe-mem-close]'), 'click', () => this.close());
    on(this.querySelector('[data-pe-mem-edit]'), 'click', () => this.close());
    on(this.dialog, 'cancel', event => { if (this.adding) event.preventDefault(); });
    on(this.dialog, 'close', () => {
      if (!this.adding) this.previewButton.focus();
    });
    on(this.dialog, 'click', event => { if (event.target === this.dialog) this.close(); });
    on(this.quantityInput, 'input', () => { this.clearError(); this.updateQuantityButtons(); });
    on(this.quantityMinus, 'click', () => this.stepQuantity(-1));
    on(this.quantityPlus, 'click', () => this.stepQuantity(1));
    const prepare = event => {
      if (!event.target.matches?.('input[type="text"], input[type="radio"], select')) return;
      void this.preparePreview();
      if (['input', 'change', 'compositionend'].includes(event.type) && !event.isComposing) {
        const immediate = event.type === 'change' && this.optionInputs.some(control => control.kind === 'finish' && control.radios?.includes(event.target));
        this.schedulePreviewRender({ immediate });
      }
    };
    for (const name of ['focusin', 'pointerdown', 'input', 'change', 'compositionend']) on(this.controlRoot, name, prepare);
    on(document, 'pe:cart:before-add', event => {
      const source = event.target;
      const id = event.detail?.formData?.get('id');
      if (this.ownsForm(source) || this.product.variants.some(v => String(v.id) === String(id))) {
        event.preventDefault();
        this.requestAdd();
      }
    });
    on(window, 'submit', event => {
      const form = event.target;
      if (form instanceof HTMLFormElement && this.ownsForm(form) && /\/cart\/add/.test(form.action)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.requestAdd();
      }
    }, true);
    on(window, 'click', event => {
      const button = event.target.closest?.('.main-product-atc, .product-form__submit, .shopify-payment-button');
      if (button && !this.contains(button) && (this.ownsForm(button.form) || button.getAttribute('form') === `product-form-${this.dataset.sectionId}`)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.requestAdd();
      }
    }, true);
    this.observeDesignGroups();
    this.load();
  }

  disconnectedCallback() {
    this.connected = false;
    this.designObserver?.disconnect();
    this.designObserver = null;
    this.restoreDesignGroup();
    this.abort?.abort();
    this.stopPreviewWork();
    this.renderToken += 1;
    this.configGeneration = (this.configGeneration || 0) + 1;
    this.loading = false;
    this.rendering = false;
    this.renderJob = null;
    this.foregroundRender = null;
    this.previewConfig = null;
    this.previewConfigPromise = null;
    this.engine?.destroy();
    this.engine = null;
    this.preparations = null;
    this.nativePrice?.removeAttribute('data-pe-mem-replaced-price');
    if (!this.section?.querySelector('pe-mem-modal')) this.section?.classList.remove('pe-mem-modal-owned');
  }

  get controlRoot() { return this.querySelector('[data-pe-mem-controls]'); }
  get skipArtworkUploads() { return this.dataset.skipArtworkUploads === 'true'; }
  observeDesignGroups() {
    this.designSlot = this.querySelector('[data-pe-mem-design-slot]');
    if (!this.designSlot || !this.section || typeof MutationObserver === 'undefined') return;
    this.designObserver = new MutationObserver(records => {
      const containsDesign = node => node.nodeType === 1 && (node.matches('pe-design-groups') || node.querySelector('pe-design-groups'));
      const marker = this.designGroupMount?.marker;
      if (records.some(record => {
        const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
        if (target?.closest('pe-design-groups')) return true;
        if (record.type === 'attributes') return containsDesign(target) || (marker && target?.contains(marker));
        return [...record.addedNodes, ...record.removedNodes].some(node => containsDesign(node) || (marker && node.contains(marker)));
      })) this.syncDesignGroup();
    });
    this.designObserver.observe(this.section, {
      childList: true, subtree: true, characterData: true, attributes: true,
      attributeFilter: ['hidden', 'style', 'class', 'data-product-id', 'data-section-id'],
    });
  }

  designGroupVisible(node, stop = this.section) {
    for (let current = node; current && current !== stop.parentElement; current = current.parentElement) {
      const style = getComputedStyle(current);
      if (current.hidden || style.display === 'none' || style.visibility === 'hidden') return false;
      if (current === stop) break;
    }
    return true;
  }

  usableDesignGroup(selector) {
    return selector.dataset.productId === String(this.product.id)
      && selector.dataset.sectionId === this.dataset.sectionId
      && selector.querySelector('.pe-design-groups__heading')
      && [...selector.querySelectorAll('a[data-design-id][href]')].some(link => this.designGroupVisible(link, selector))
      && this.designGroupVisible(selector);
  }

  restoreDesignGroup() {
    const mounted = this.designGroupMount;
    if (!mounted) return;
    this.designGroupMount = null;
    if (mounted.heading.textContent === mounted.numberedText) mounted.heading.textContent = mounted.originalText;
    if (this.designSlot?.contains(mounted.node)) {
      if (mounted.marker.parentNode) mounted.marker.replaceWith(mounted.node);
      else mounted.node.remove();
    } else mounted.marker.remove();
    if (this.designSlot && !this.designSlot.hidden) this.designSlot.hidden = true;
    this.updateStepNumbers();
  }

  syncDesignGroup() {
    if (!this.designSlot || !this.section || !this.isConnected) return;
    const ready = Boolean(this.config && !this.controlRoot.hidden);
    const mounted = this.designGroupMount;
    if (mounted && (!ready || !this.designSlot.contains(mounted.node) || !mounted.node.contains(mounted.selector) || !this.section.contains(mounted.marker) || !this.designGroupVisible(mounted.marker.parentElement) || !this.usableDesignGroup(mounted.selector))) {
      this.restoreDesignGroup();
    }
    if (ready && !this.designGroupMount) {
      const selector = [...this.section.querySelectorAll('pe-design-groups')].find(candidate => this.usableDesignGroup(candidate));
      if (selector) {
        const wrapper = selector.closest('.shopify-app-block');
        const node = wrapper && this.section.contains(wrapper) && !wrapper.contains(this) ? wrapper : selector;
        const marker = document.createComment('');
        const heading = selector.querySelector('.pe-design-groups__heading');
        node.before(marker);
        this.designGroupMount = { selector, node, marker, heading, originalText: heading.textContent };
        this.designSlot.hidden = false;
        const active = node.contains(document.activeElement) ? document.activeElement : null;
        this.designSlot.append(node);
        active?.focus({ preventScroll: true });
      }
    }
    const active = this.designGroupMount;
    if (active) {
      const heading = active.selector.querySelector('.pe-design-groups__heading');
      if (heading !== active.heading || (active.numberedText && heading.textContent !== active.numberedText)) {
        active.heading = heading;
        active.originalText = heading.textContent;
      }
      active.numberedText = `1. ${active.originalText.replace(/^\d+\.\s+/, '')}`;
      if (heading.textContent !== active.numberedText) heading.textContent = active.numberedText;
    }
    this.updateStepNumbers();
  }

  updateStepNumbers() {
    const offset = this.designGroupMount ? 1 : 0;
    (this.optionHeadings || []).forEach(({ node, label }, index) => {
      const text = `${index + offset + 1}. ${label}`;
      if (node.textContent !== text) node.textContent = text;
    });
    const heading = this.querySelector('[data-pe-mem-fields-title]');
    const text = `${(this.product?.options.length || 0) + offset + 1}. ${this.message('make_yours')}`;
    if (heading && heading.textContent !== text) heading.textContent = text;
  }

  message(key) { return this.messages[key] || this.messages.load_error; }
  setText(selector, text = '') {
    const node = this.querySelector(selector);
    node.classList.remove('pe-mem-modal__status--quiet');
    node.textContent = text;
    node.hidden = !text;
  }
  error(key, details = '') {
    const messageKey = ['conflict', 'duplicate'].includes(key) && this.dataset.designMode !== 'true' ? 'unsupported' : key;
    this.setText('[data-pe-mem-error]', this.message(messageKey));
    if (this.dataset.designMode === 'true' && details) {
      const extra = document.createElement('p');
      extra.textContent = details;
      this.querySelector('[data-pe-mem-error]').append(extra);
    }
  }
  clearError() { if (!this.uncertain) this.setText('[data-pe-mem-error]'); }
  ownsForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    if (form.id === `product-form-${this.dataset.sectionId}`) return true;
    const id = new FormData(form).get('id');
    return this.product.variants.some(variant => String(variant.id) === String(id));
  }
  price(variant) {
    return new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(variant.price / 100);
  }
  conflict() {
    const others = [...document.querySelectorAll('pe-mem-modal')].filter(node => node !== this);
    if (others.length) return 'duplicate';
    if (this.section?.querySelector('.mem_widget_ui, [id="mem-form"], [id="mem-widget"]')) return 'conflict';
    return '';
  }

  async load() {
    if (this.loading || this.adding) return;
    this.loading = true;
    this.restoreDesignGroup();
    this.stopPreviewWork();
    const generation = this.configGeneration = (this.configGeneration || 0) + 1;
    this.renderToken += 1;
    this.rendering = false;
    this.renderJob = null;
    this.foregroundRender = null;
    this.config = null;
    this.previewConfig = null;
    this.previewConfigPromise = null;
    this.result = null;
    this.engine?.destroy();
    this.engine = null;
    this.preparations = null;
    this.controlRoot.hidden = true;
    this.previewButton.disabled = true;
    if (this.mainAddButton) this.mainAddButton.disabled = true;
    this.clearError();
    this.querySelector('[data-pe-mem-retry]').hidden = true;
    this.setText('[data-pe-mem-loading]');
    try {
      const conflict = this.conflict();
      if (conflict) { this.error(conflict); return; }
      const fullSource = this.querySelector('[data-pe-mem-config]')?.textContent;
      let config = fullSource?.trim()
        ? loadMetafieldConfig({ productId: this.product.id, source: fullSource })
        : loadLegacyMetafieldConfig({ productId: this.product.id, source: this.querySelector('[data-pe-mem-legacy-fields]')?.textContent });
      if (!config) {
        this.setText('[data-pe-mem-loading]', this.message('loading'));
        config = await loadConfig({ productId: this.product.id, shop: this.dataset.shop, cacheVersion: this.dataset.cacheVersion });
      }
      if (!this.isConnected || generation !== this.configGeneration) return;
      this.config = config;
      this.buildControls();
      this.controlRoot.hidden = false;
      this.updateVariant();
      this.syncDesignGroup();
    } catch (error) {
      if (!this.isConnected || generation !== this.configGeneration) return;
      this.error(error.code === 'UNSUPPORTED_DESIGN' ? 'unsupported' : 'load_error', error.message);
      this.querySelector('[data-pe-mem-retry]').hidden = error.code === 'UNSUPPORTED_DESIGN';
    } finally {
      if (generation === this.configGeneration) {
        this.loading = false;
        this.setText('[data-pe-mem-loading]');
        if (this.config && this.isConnected) this.schedulePreviewPreparation();
      }
    }
  }

  buildControls() {
    const optionsHost = this.querySelector('[data-pe-mem-options]');
    const fieldsHost = this.querySelector('[data-pe-mem-fields]');
    const sizeHelp = this.querySelector('[data-pe-mem-size-help]');
    if (sizeHelp) { sizeHelp.hidden = true; optionsHost.after(sizeHelp); }
    optionsHost.replaceChildren();
    fieldsHost.replaceChildren();
    this.inputs.clear();
    this.optionInputs = [];
    this.optionHeadings = [];
    this.values = {};
    const initial = this.product.variants.find(v => String(v.id) === this.dataset.initialVariant) || this.product.variants.find(v => v.available) || this.product.variants[0];
    this.product.options.forEach((option, index) => {
      const name = String(typeof option === 'string' ? option : option.name).trim().replace(/\s+/g, ' ');
      const values = [...new Set(this.product.variants.map(v => (v.options || [v.option1, v.option2, v.option3])[index]))];
      const kind = /^(colou?r|finish)$/i.test(name) ? 'finish' : /^size(?:\s*\((?:measured by )?in(?:ch(?:es)?)?\))?$/i.test(name) ? 'size' : 'other';
      const wrapper = document.createElement('fieldset');
      wrapper.className = 'pe-mem-modal__step';
      const legend = document.createElement('legend');
      legend.className = 'pe-mem-modal__step-title';
      const stepLabel = kind === 'finish' ? this.message('choose_finish') : kind === 'size' ? this.message('choose_size') : name;
      legend.textContent = `${index + 1}. ${stepLabel}`;
      this.optionHeadings.push({ node: legend, label: stepLabel });
      wrapper.append(legend);
      const selected = (initial.options || [initial.option1, initial.option2, initial.option3])[index];
      if (kind === 'other') {
        const select = document.createElement('select');
        select.id = `${this.id}-option-${index}`;
        select.setAttribute('aria-label', name);
        values.forEach(value => select.add(new Option(value, value)));
        select.value = selected;
        select.addEventListener('change', () => { this.invalidate(); this.updateVariant(); }, { signal: this.abort.signal });
        wrapper.append(select);
        this.optionInputs.push(select);
      } else {
        const choices = document.createElement('div');
        choices.className = kind === 'finish' ? 'pe-mem-modal__swatches' : 'pe-mem-modal__sizes';
        const badgeLabel = (this.dataset.sizeBadgeLabel || '').trim();
        const popularSize = kind === 'size' && this.dataset.showSizeBadge === 'true' && this.dataset.popularSize?.trim() && badgeLabel
          ? values.find(value => this.sizeKey(value) === this.sizeKey(this.dataset.popularSize))
          : undefined;
        if (popularSize !== undefined) choices.classList.add('pe-mem-modal__sizes--badged');
        const control = { value: selected, index, kind, radios: [] };
        values.forEach((value, valueIndex) => {
          const label = document.createElement('label');
          label.className = kind === 'finish' ? 'pe-mem-modal__choice' : 'pe-mem-modal__size';
          const radio = document.createElement('input');
          radio.type = 'radio';
          radio.name = `${this.id}-option-${index}`;
          radio.id = `${radio.name}-${valueIndex}`;
          radio.value = value;
          radio.checked = value === selected;
          label.htmlFor = radio.id;
          const visual = document.createElement('span');
          visual.className = kind === 'finish' ? 'pe-mem-modal__swatch' : 'pe-mem-modal__size-label';
          label.append(radio, visual);
          if (kind === 'finish') {
            visual.setAttribute('aria-hidden', 'true');
            visual.style.setProperty('--pe-mem-swatch', this.swatch(value));
            const caption = document.createElement('span');
            caption.className = 'pe-mem-modal__choice-name';
            caption.textContent = value;
            label.append(caption);
          } else {
            visual.textContent = /^\d+(\.\d+)?$/.test(value) ? `${value}\u2033` : value;
            if (value === popularSize) {
              label.classList.add('pe-mem-modal__size--popular');
              const badge = document.createElement('span');
              badge.className = 'pe-mem-modal__size-badge';
              badge.textContent = badgeLabel;
              label.append(badge);
            }
          }
          radio.addEventListener('change', () => {
            if (!radio.checked) return;
            control.value = value;
            this.invalidate();
            this.updateVariant();
          }, { signal: this.abort.signal });
          control.radios.push(radio);
          choices.append(label);
        });
        wrapper.append(choices);
        this.optionInputs.push(control);
      }
      optionsHost.append(wrapper);
      if (kind === 'size' && sizeHelp) { sizeHelp.hidden = false; wrapper.append(sizeHelp); }
    });
    this.updateStepNumbers();
    this.config.fields.forEach((field, index) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'pe-mem-modal__field';
      const label = document.createElement('label');
      const isSelect = field.type === 'select';
      const input = document.createElement(isSelect ? 'select' : 'input');
      const count = document.createElement('span');
      const header = document.createElement('div');
      header.className = 'pe-mem-modal__field-header';
      input.id = `${this.id}-field-${index}`;
      if (isSelect) {
        const placeholder = new Option(field.placeholder || field.label, '');
        input.add(placeholder);
        field.options.forEach(value => input.add(new Option(value, value)));
        input.required = false;
        input.value = '';
      } else {
        input.type = 'text';
        input.placeholder = field.placeholder || '';
        input.required = false;
        input.autocomplete = 'off';
        input.spellcheck = false;
        input.value = field.defaultValue || '';
        input.setAttribute('aria-describedby', `${input.id}-count`);
      }
      label.htmlFor = input.id;
      label.textContent = field.label;
      count.id = `${input.id}-count`;
      count.className = 'pe-mem-modal__counter';
      const update = () => {
        if (!isSelect && field.capitalization === 'uppercase') input.value = input.value.toUpperCase();
        if (!isSelect && field.capitalization === 'lowercase') input.value = input.value.toLowerCase();
        this.values[field.id] = input.value;
        count.textContent = field.maxLength ? `${input.value.length} / ${field.maxLength}` : `${input.value.length} ${this.message('characters')}`;
        input.setCustomValidity('');
      };
      input.addEventListener(isSelect ? 'change' : 'input', () => { update(); this.invalidate(); }, { signal: this.abort.signal });
      update();
      this.inputs.set(String(field.id), input);
      header.append(label);
      if (!isSelect) header.append(count);
      wrapper.append(header, input);
      fieldsHost.append(wrapper);
    });
  }

  sizeKey(value) {
    const text = String(value).trim().toLowerCase();
    const inches = text.match(/^(\d+(?:\.\d+)?)\s*(?:["\u2033\u201d]|in(?:ch(?:es)?)?\.?)?$/);
    return inches ? `${Number(inches[1])}in` : text;
  }

  swatch(value) {
    const colors = { black: '#242424', copper: '#905e43', gold: '#b08a43', white: '#ebebeb', silver: '#aaa9a5', red: '#a62a32', pink: '#e493b1', blue: '#3e719e', purple: '#785590', yellow: '#e1ba37', rose: '#b87476', orange: '#ce773e', stone: '#9e988c', green: '#53705b', brown: '#78513a', 'rustic brown': '#78513a', 'antique gold': '#b08a43', 'brushed silver': '#aaa9a5' };
    const color = colors[String(value).toLowerCase()] || '#c5c3bd';
    return `radial-gradient(circle at 28% 18%, #ffffff50, transparent 65%), linear-gradient(145deg, ${color}, ${color})`;
  }

  currentVariant() {
    const selected = this.optionInputs.map(input => input.value);
    return this.product.variants.find(variant => (variant.options || [variant.option1, variant.option2, variant.option3]).slice(0, selected.length).every((value, index) => value === selected[index]));
  }

  updateVariant() {
    const variant = this.currentVariant();
    this.previewButton.disabled = !variant?.available || this.rendering || this.adding || this.uncertain;
    if (this.mainAddButton) this.mainAddButton.disabled = this.previewButton.disabled;
    this.optionInputs.forEach(control => control.radios?.forEach(radio => {
      radio.disabled = !this.product.variants.some(candidate => {
        if (!candidate.available) return false;
        const options = candidate.options || [candidate.option1, candidate.option2, candidate.option3];
        return options[control.index] === radio.value && (control.kind === 'finish' || this.optionInputs.every((other, index) => index === control.index || options[index] === other.value));
      });
    }));
    if (!variant?.available) this.error('unavailable');
    this.setText('[data-pe-mem-price]', variant ? this.price(variant) : '');
    const rule = variant?.quantity_rule || { min: 1, increment: 1, max: null };
    this.quantityInput.min = rule.min || 1;
    this.quantityInput.step = rule.increment || 1;
    if (rule.max) this.quantityInput.max = rule.max; else this.quantityInput.removeAttribute('max');
    this.updateQuantityButtons();
  }

  quantityRules() {
    const min = Math.max(1, Number(this.quantityInput.min) || 1);
    const step = Math.max(1, Number(this.quantityInput.step) || 1);
    const maximum = this.quantityInput.max ? Number(this.quantityInput.max) : Infinity;
    const max = Number.isFinite(maximum) ? min + Math.floor((maximum - min) / step) * step : Infinity;
    return { min, step, max };
  }

  updateQuantityButtons() {
    const { min, max } = this.quantityRules();
    const value = Number(this.quantityInput.value);
    const locked = Boolean(this.adding || this.uncertain);
    if (this.quantityMinus) this.quantityMinus.disabled = locked || value <= min;
    if (this.quantityPlus) this.quantityPlus.disabled = locked || value >= max;
    this.quantityInput.disabled = locked;
  }

  stepQuantity(direction) {
    if (this.adding || this.uncertain) return;
    const { min, step, max } = this.quantityRules();
    const value = Number(this.quantityInput.value);
    const position = (value - min) / step;
    const next = !Number.isFinite(value) || value < min ? min : min + (direction > 0 ? Math.floor(position) + 1 : Math.ceil(position) - 1) * step;
    this.quantityInput.value = String(Math.max(min, Math.min(max, next)));
    this.clearError();
    this.updateQuantityButtons();
  }

  invalidate() {
    this.result = null;
    this.resultKey = '';
    this.uploaded = null;
    this.confirmButton.disabled = true;
    this.clearError();
    this.setText('[data-pe-mem-status]');
  }

  validatedSelection({ quiet = false } = {}) {
    const variant = this.currentVariant();
    if (!variant?.available || variant.requires_selling_plan) { if (!quiet) this.error('unavailable'); return null; }
    const quantity = Number(this.quantityInput.value);
    if (!Number.isInteger(quantity) || quantity < 1 || !this.quantityInput.checkValidity()) {
      if (!quiet) {
        this.error('quantity_error');
        this.quantityInput.reportValidity();
      }
      return null;
    }
    if (!quiet) this.inputs.forEach(input => input.setCustomValidity(''));
    const textInputs = this.config.fields.filter(field => field.type !== 'select').map(field => this.inputs.get(String(field.id)));
    if (!textInputs.some(input => input.value.trim())) {
      if (quiet) return null;
      this.error('required');
      const input = textInputs[0];
      if (input) {
        input.setCustomValidity(this.message('required'));
        input.reportValidity();
      }
      return null;
    }
    try {
      const values = normalizeValues(this.config, this.values);
      if (!quiet) {
        for (const [id, value] of Object.entries(values)) {
          this.values[id] = value;
          const input = this.inputs.get(id);
          if (input) input.value = value;
        }
      }
      return { variant, quantity, values, key: JSON.stringify([String(variant.id), values]) };
    } catch { if (!quiet) this.error('invalid_text'); return null; }
  }

  stopPreviewWork() {
    clearTimeout(this.previewRenderTimer);
    clearTimeout(this.previewPrepareTimer);
    if (this.previewIdle !== undefined) window.cancelIdleCallback?.(this.previewIdle);
    this.previewObserver?.disconnect();
    if (this.previewLoadHandler) window.removeEventListener('load', this.previewLoadHandler);
    this.previewRenderTimer = this.previewPrepareTimer = this.previewIdle = undefined;
    this.previewObserver = this.previewLoadHandler = null;
    this.queuedPreview = this.backgroundPreview = null;
  }

  schedulePreviewPreparation() {
    if (window.navigator?.connection?.saveData) return;
    const generation = this.configGeneration;
    const prepare = () => {
      if (generation !== this.configGeneration || !this.isConnected) return;
      this.previewIdle = this.previewPrepareTimer = undefined;
      if (!document.hidden) void this.preparePreview();
    };
    const whenLoaded = () => {
      if (generation !== this.configGeneration || !this.isConnected) return;
      this.previewLoadHandler = null;
      if (window.requestIdleCallback) this.previewIdle = window.requestIdleCallback(prepare, { timeout: 1500 });
      else this.previewPrepareTimer = setTimeout(prepare, 600);
    };
    let approached = false;
    const nearForm = () => {
      if (approached || generation !== this.configGeneration || !this.isConnected) return;
      approached = true;
      this.previewObserver?.disconnect();
      this.previewObserver = null;
      if (document.readyState === 'complete') whenLoaded();
      else {
        this.previewLoadHandler = whenLoaded;
        window.addEventListener('load', whenLoaded, { once: true });
      }
    };
    if (typeof IntersectionObserver === 'function') {
      this.previewObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) nearForm();
      }, { rootMargin: '200px' });
      this.previewObserver.observe(this);
    } else nearForm();
  }

  schedulePreviewRender({ immediate = false } = {}) {
    clearTimeout(this.previewRenderTimer);
    const generation = this.configGeneration;
    this.previewRenderTimer = setTimeout(() => {
      this.previewRenderTimer = undefined;
      if (!this.config || !this.isConnected || generation !== this.configGeneration || this.loading || this.adding || this.rendering || this.uncertain || document.hidden) return;
      const selected = this.validatedSelection({ quiet: true });
      this.queuedPreview = selected;
      if (!selected || this.backgroundPreview) return;
      const pending = (async () => {
        while (this.queuedPreview && this.isConnected && generation === this.configGeneration && !this.loading && !this.adding && !this.rendering && !this.uncertain && !document.hidden) {
          const next = this.queuedPreview;
          this.queuedPreview = null;
          await this.renderSelection(next, { background: true, includeProduction: false }).catch(() => {});
        }
      })().finally(() => {
        if (this.backgroundPreview === pending) this.queuedPreview = this.backgroundPreview = null;
      });
      this.backgroundPreview = pending;
    }, immediate ? 0 : 350);
  }

  ensurePreviewConfig() {
    if (!this.config?.fieldsOnly) return Promise.resolve(this.config);
    if (this.previewConfig) return Promise.resolve(this.previewConfig);
    if (this.previewConfigPromise) return this.previewConfigPromise;
    const fieldsConfig = this.config;
    const generation = this.configGeneration;
    const pending = loadConfig({ productId: this.product.id, shop: this.dataset.shop, cacheVersion: this.dataset.cacheVersion }).then(config => {
      if (!this.isConnected || generation !== this.configGeneration || this.config !== fieldsConfig) throw new Error('stale');
      this.previewConfig = assertMatchingLegacyFields(fieldsConfig, config);
      return this.previewConfig;
    }).finally(() => {
      if (this.previewConfigPromise === pending) this.previewConfigPromise = null;
    });
    this.previewConfigPromise = pending;
    return pending;
  }

  getEngine() {
    const config = this.previewConfig || this.config;
    if (config?.fieldsOnly) throw new Error('The preview configuration is not ready.');
    if (!this.engine) this.engine = createEngine({ host: this.querySelector('[data-pe-mem-renderer]'), config, frameUrl: this.dataset.frameUrl, title: this.message('preview_alt') });
    return this.engine;
  }

  preparePreview() {
    if (!this.config || !this.isConnected || this.loading || this.adding || this.uncertain) return;
    const variant = this.currentVariant();
    if (!variant?.available || variant.requires_selling_plan) return;
    if (this.config.fieldsOnly && !this.previewConfig) {
      const config = this.config;
      return this.ensurePreviewConfig().then(() => {
        if (this.config === config && this.isConnected) return this.preparePreview();
      }).catch(() => {});
    }
    try {
      const engine = this.getEngine();
      if (this.preparations?.engine !== engine) this.preparations = { engine, variants: new Map() };
      const variants = this.preparations.variants;
      const key = String(variant.id);
      if (!variants.has(key)) {
        // Optional preparation never changes the form or interrupts a render.
        const pending = engine.prepare({ variant }).catch(() => {
          if (variants.get(key) === pending) variants.delete(key);
        });
        variants.set(key, pending);
      }
      return variants.get(key);
    } catch { /* Preview can retry initialization when explicitly requested. */ }
  }

  async renderSelection(selected, { background = false, includeProduction = !this.skipArtworkUploads } = {}) {
    const generation = this.configGeneration;
    const config = this.config;
    const current = () => {
      if (!this.isConnected || generation !== this.configGeneration || config !== this.config || selected.key !== this.validatedSelection({ quiet: true })?.key) throw new Error('stale');
    };
    const cached = () => this.result && this.resultKey === selected.key && (!includeProduction || this.result.productionSvg);
    current();
    if (cached()) return this.result;
    const foreground = background ? null : {};
    if (foreground) {
      this.foregroundRender = foreground;
      this.rendering = true;
      this.updateVariant();
    }
    try {
      while (this.renderJob) {
        await this.renderJob.promise.catch(() => {});
        current();
        if (cached()) return this.result;
      }
      if (background && (this.adding || this.rendering || this.uncertain || document.hidden)) throw new Error('stale');
      const token = ++this.renderToken;
      const job = { key: selected.key, includeProduction };
      this.renderJob = job;
      job.promise = (async () => {
        let engine;
        try {
          if (config.fieldsOnly && !this.previewConfig) await this.ensurePreviewConfig();
          current();
          engine = this.getEngine();
          const result = await engine.render({ values: selected.values, variant: selected.variant, includeProduction });
          current();
          if (token !== this.renderToken) throw new Error('stale');
          this.result = result;
          this.resultKey = selected.key;
          return result;
        } catch (error) {
          if (error.message !== 'stale' && generation === this.configGeneration && token === this.renderToken && config === this.config) {
            this.result = null;
            if (engine && this.engine === engine) { engine.destroy(); this.engine = null; }
          }
          throw error;
        } finally {
          if (this.renderJob === job) this.renderJob = null;
        }
      })();
      return await job.promise;
    } finally {
      if (foreground && this.foregroundRender === foreground) {
        this.foregroundRender = null;
        this.rendering = false;
        this.updateVariant();
      }
    }
  }

  async ensureUploaded(selected, result) {
    if (this.uploaded?.key === selected.key && this.uploaded.result === result) return this.uploaded.urls;
    const pending = this.uploads || (this.uploads = new WeakMap());
    if (pending.has(result)) return pending.get(result);
    const saving = uploadArtwork({ result, shop: this.dataset.shop }).then(urls => {
      // An earlier preview may finish saving after the customer has edited it.
      if (this.isConnected && this.result === result && this.resultKey === selected.key) {
        this.uploaded = { key: selected.key, result, urls };
      }
      return urls;
    }).finally(() => pending.delete(result));
    pending.set(result, saving);
    return saving;
  }

  async preview() {
    if (!this.config || this.rendering || this.adding || this.uncertain) return;
    const conflict = this.conflict();
    if (conflict) { this.error(conflict); return; }
    const selected = this.validatedSelection();
    if (!selected) return;
    const config = this.config;
    const generation = this.configGeneration;
    this.clearError();
    this.setText('[data-pe-mem-dialog-error]');
    this.confirmButton.textContent = this.message('add');
    this.confirmButton.disabled = true;
    const display = this.querySelector('[data-pe-mem-engine]');
    display.replaceChildren();
    if (!this.dialog.open) this.dialog.showModal();
    this.setText('[data-pe-mem-render-status]', this.message('generating'));
    try {
      const result = await this.renderSelection(selected);
      if (!this.isConnected || !this.dialog.open || config !== this.config || generation !== this.configGeneration) return;
      const image = document.createElement('img');
      image.alt = this.message('preview_alt');
      image.width = result.width;
      image.height = result.height;
      image.src = result.previewDataUrl;
      display.replaceChildren(image);
      this.confirmButton.disabled = false;
      // Show the preview immediately; Add shares this upload if clicked meanwhile.
      // A failed background save is retried only on a later explicit action.
      if (!this.skipArtworkUploads) void this.ensureUploaded(selected, result).catch(() => {});
    } catch (error) {
      if (this.isConnected && this.dialog.open && config === this.config && generation === this.configGeneration) {
        const detail = this.dataset.designMode === 'true' ? ` ${error.message}` : '';
        const message = error.message === 'stale' ? 'stale' : this.skipArtworkUploads ? 'preview_error_text_only' : 'preview_error';
        this.setText('[data-pe-mem-dialog-error]', this.message(message) + detail);
        this.confirmButton.disabled = !this.skipArtworkUploads || this.uncertain;
      }
    } finally {
      if (config === this.config && generation === this.configGeneration) this.setText('[data-pe-mem-render-status]');
    }
  }

  close() { if (!this.adding) this.dialog.close(); }

  requestAdd() { return this.add(); }

  async add() {
    if (!this.config || this.adding || this.rendering || this.uncertain) return;
    const conflict = this.conflict();
    if (conflict) { this.error(conflict); return; }
    const selected = this.validatedSelection();
    if (!selected) return;
    const drawer = document.querySelector('pe-cart-drawer');
    if (drawer?.busy || drawer?.checkingOut) { this.error('cart_busy'); return; }
    const trigger = this.dialog.open ? this.previewButton : this.mainAddButton || this.previewButton;
    const canLockDrawer = drawer && ['setBusy', 'open', 'message'].every(method => typeof drawer[method] === 'function');
    let ownsDrawer = false;
    let openCart = false;
    let cartStarted = false;
    let cartAccepted = false;
    let phase = 'render';
    const progress = key => {
      const text = this.message(key);
      this.confirmButton.textContent = text;
      if (this.mainAddButton) this.mainAddButton.textContent = text;
      this.setText('[data-pe-mem-status]', text);
      this.querySelector('[data-pe-mem-status]').classList.add('pe-mem-modal__status--quiet');
      if (ownsDrawer) drawer.message(text);
    };
    const fail = (key, details = '') => {
      const text = details || this.message(key);
      this.error(key);
      if (details) this.setText('[data-pe-mem-error]', text);
      this.setText('[data-pe-mem-dialog-error]', text);
      this.setText('[data-pe-mem-status]');
      if (ownsDrawer) drawer.message(text, true);
    };
    this.adding = true;
    this.clearError();
    this.querySelector('[data-pe-mem-cart-link]').hidden = true;
    this.updateVariant();
    this.confirmButton.disabled = true;
    this.setText('[data-pe-mem-dialog-error]');
    this.querySelectorAll('[data-pe-mem-close], [data-pe-mem-edit]').forEach(button => { button.disabled = true; });
    try {
      if (this.dialog.open) this.dialog.close();
      if (canLockDrawer) {
        drawer.setBusy(true);
        ownsDrawer = true;
      }
      let properties;
      if (this.skipArtworkUploads) {
        phase = 'properties';
        progress('adding');
        properties = buildProperties(this.config, selected.values);
      } else {
        progress('uploading');
        const result = await this.renderSelection(selected);
        if (!this.isConnected) return;
        phase = 'upload';
        progress('uploading');
        const urls = await this.ensureUploaded(selected, result);
        properties = { ...result.properties };
        properties[result.filePropertyKeys.preview] = urls.preview;
        properties[result.filePropertyKeys.production] = urls.production;
      }
      if (!this.isConnected) return;
      const current = this.validatedSelection();
      if (!current || selected.key !== current.key) throw new Error('stale');
      const payload = { items: [{ id: selected.variant.id, quantity: current.quantity, properties }] };
      if (drawer && typeof drawer.sections === 'function') {
        payload.sections = drawer.sections();
        payload.sections_url = drawer.dataset.cartUrl || this.dataset.cartUrl;
      }
      progress('adding');
      cartStarted = true;
      const root = (window.Shopify?.routes?.root || this.dataset.rootUrl || '/').replace(/\/?$/, '/');
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (response.status >= 500) throw new Error('Cart confirmation is uncertain.');
      if (!response.ok || Number(data.status) >= 400) {
        cartStarted = false;
        fail('add_error', typeof data.description === 'string' ? data.description : '');
        return;
      }
      cartAccepted = true;
      this.result = null;
      this.uploaded = null;
      this.setText('[data-pe-mem-status]');
      if (drawer && typeof drawer.receive === 'function' && typeof drawer.open === 'function') {
        await drawer.receive(data);
        if (ownsDrawer) drawer.message(this.message('added'));
      } else this.querySelector('[data-pe-mem-cart-link]').hidden = false;
      openCart = true;
    } catch (error) {
      if (cartAccepted) {
        this.setText('[data-pe-mem-status]');
        this.setText('[data-pe-mem-error]', this.message('drawer_error'));
        this.querySelector('[data-pe-mem-cart-link]').hidden = false;
        if (ownsDrawer) drawer.message(this.message('drawer_error'), true);
      } else if (cartStarted) {
        this.uncertain = true;
        if (ownsDrawer && typeof drawer.refresh === 'function') await drawer.refresh().catch(() => {});
        fail('uncertain');
        this.querySelector('[data-pe-mem-cart-link]').hidden = false;
      } else {
        fail(error.message === 'stale' ? 'stale' : phase === 'properties' ? 'invalid_text' : phase === 'render' ? 'preview_error' : 'upload_error');
      }
    } finally {
      if (!cartStarted && !cartAccepted && !this.isConnected && ownsDrawer) drawer.message(this.message('cancelled'), true);
      if (ownsDrawer) drawer.setBusy(false);
      this.adding = false;
      this.confirmButton.textContent = this.message('add');
      if (this.mainAddButton) this.mainAddButton.textContent = this.message('add');
      this.confirmButton.disabled = (!this.skipArtworkUploads && !this.result) || this.uncertain;
      this.querySelectorAll('[data-pe-mem-close], [data-pe-mem-edit]').forEach(button => { button.disabled = false; });
      this.updateVariant();
      if (openCart) {
        try {
          if (drawer && typeof drawer.open === 'function') drawer.open(trigger, false);
          else document.dispatchEvent(new CustomEvent('pe:cart:open', { detail: { trigger } }));
        } catch {
          this.setText('[data-pe-mem-error]', this.message('drawer_error'));
          this.querySelector('[data-pe-mem-cart-link]').hidden = false;
        }
      }
    }
  }
}

if (!customElements.get('pe-mem-modal')) customElements.define('pe-mem-modal', PeMemModal);


